import { inject, Injectable, signal } from '@angular/core';
import { DevPortalHttpClient } from './dev-portal-http-client.service';
import { catchError, throwError, finalize, tap } from 'rxjs';
import { Persona } from '../models/persona.model';
import { ErrorHandler } from '../utilities/error-handler/error-handler';

@Injectable({
    providedIn: 'root',
})
export class PersonaService {
    private readonly httpClient = inject(DevPortalHttpClient);
    private readonly errorHandler = inject(ErrorHandler);

    // Service states
    private readonly _isLoading = signal(false);
    private readonly _personas = signal<Persona[]>([]);
    private readonly _activePersona = signal<Persona | undefined>(undefined);
    private hasFetched = false;

    readonly personas = this._personas.asReadonly();
    readonly activePersona = this._activePersona.asReadonly();
    readonly isLoading = this._isLoading.asReadonly();

    /**
     * Loads personas from the API and updates the personas signal.
     * Subsequent calls are ignored unless forceRefresh is true.
     * Consumers should read from the personas() signal.
     */
    loadPersonas(forceRefresh = false): void {
        // Skip if already fetched and not forcing refresh
        if (this.hasFetched && !forceRefresh) {
            return;
        }

        this._isLoading.set(true);
        this.httpClient.Get<Persona[]>('/personas')
            .pipe(
                tap(() => console.log('Fetching personas')),
                catchError((error) => {
                    console.error('Error fetching personas:', error);
                    this.errorHandler.showError(
                        'An unexpected error happened while fetching personas.'
                    );
                    return throwError(() => error);
                }),
                finalize(() => {
                    this._isLoading.set(false);
                })
            )
            .subscribe((personaList) => {
                this._personas.set(personaList);
                this.hasFetched = true;
            });
    }

    setActivePersona(persona: Persona): void {
        this._activePersona.set(persona);
    }

    clearActivePersona(): void {
        this._activePersona.set(undefined);
    }
}
